/**
 * Bottom-center action confirmation toast.
 *
 * History: this used to wrap a 5 s undo window — the action committed
 * to the backend only after the timer expired, so the operator had a
 * grace period to click Undo. That pattern silently dropped actions
 * whenever the operator refreshed/navigated within 5 s (setTimeout
 * cancelled on unmount + process-death), which manifested as
 * "approval doesn't reach device" and "rejected row still pending
 * after refresh."
 *
 * Now the toast is purely a visual confirmation — the parent fires
 * the API call immediately at click time. The toast auto-dismisses
 * after a short hold so the operator gets feedback without blocking
 * the commit on UI state. If accidental rejects need recovery, that
 * should be a backend reverse-transition endpoint, not a client-side
 * delay.
 */

import React, { useEffect } from 'react';

export interface PendingAction {
  /** Stable key so the toast remounts (resets the hold timer) on a new action. */
  key: string;
  message: string;
  /** Override the default hold time. */
  dismissMs?: number;
}

interface UndoToastProps {
  pending: PendingAction | null;
  onResolved: () => void;
}

const DEFAULT_DISMISS_MS = 2500;

export const UndoToast: React.FC<UndoToastProps> = ({ pending, onResolved }) => {
  useEffect(() => {
    if (!pending) return;
    const dismissId = window.setTimeout(
      onResolved,
      pending.dismissMs ?? DEFAULT_DISMISS_MS,
    );
    return () => window.clearTimeout(dismissId);
  }, [pending, onResolved]);

  if (!pending) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      <div className="pointer-events-auto rounded-lg border border-border bg-bg-elevated px-4 py-2 shadow-lg">
        <span className="text-sm">{pending.message}</span>
      </div>
    </div>
  );
};

export default UndoToast;
